import { ReactNode, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CONTACT, ROUTE_PATHS } from '@/lib/index';
import { CookieBanner } from './CookieBanner';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen" style={{ fontFamily: 'var(--font-sans)' }}>
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: isScrolled ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: isScrolled ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(0,0,0,0.05)'
        }}
      >
        <nav className="w-full px-6 md:px-8 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <Link
              to="/"
              className="text-lg font-semibold tracking-tight transition-opacity duration-300 hover:opacity-60"
              style={{ color: '#1d1d1f' }}
            >
              Camille Vega de Hoyos
            </Link>

            <div className="hidden md:flex items-center gap-8">
              {isHomePage ? (
                <>
                  <a
                    href="#constat"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('constat');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="text-sm font-normal transition-opacity duration-300 hover:opacity-60 cursor-pointer"
                    style={{ color: '#1d1d1f' }}
                  >
                    Le constat
                  </a>
                  <a
                    href="#parcours"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('parcours');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="text-sm font-normal transition-opacity duration-300 hover:opacity-60 cursor-pointer"
                    style={{ color: '#1d1d1f' }}
                  >
                    Mon parcours
                  </a>
                  <a
                    href="#formation"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('formation');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="text-sm font-normal transition-opacity duration-300 hover:opacity-60 cursor-pointer"
                    style={{ color: '#1d1d1f' }}
                  >
                    La formation
                  </a>
                  <a
                    href="#pour-qui"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('pour-qui');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="text-sm font-normal transition-opacity duration-300 hover:opacity-60 cursor-pointer"
                    style={{ color: '#1d1d1f' }}
                  >
                    Pour qui
                  </a>
                </>
              ) : (
                <>
                  <Link to="/#constat" className="text-sm font-normal transition-opacity duration-300 hover:opacity-60" style={{ color: '#1d1d1f' }}>
                    Le constat
                  </Link>
                  <Link to="/#parcours" className="text-sm font-normal transition-opacity duration-300 hover:opacity-60" style={{ color: '#1d1d1f' }}>
                    Mon parcours
                  </Link>
                  <Link to="/#formation" className="text-sm font-normal transition-opacity duration-300 hover:opacity-60" style={{ color: '#1d1d1f' }}>
                    La formation
                  </Link>
                  <Link to="/#pour-qui" className="text-sm font-normal transition-opacity duration-300 hover:opacity-60" style={{ color: '#1d1d1f' }}>
                    Pour qui
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }}>
        {children}
      </main>

      <footer className="bg-[#f5f5f7] py-8 border-t border-[#d2d2d7]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#6e6e73]">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link to={ROUTE_PATHS.MENTIONS_LEGALES} className="transition-opacity hover:opacity-60">
                Mentions légales
              </Link>
              <span>·</span>
              <Link to={ROUTE_PATHS.CONFIDENTIALITE} className="transition-opacity hover:opacity-60">
                Confidentialité
              </Link>
              <span>·</span>
              <Link to={ROUTE_PATHS.COOKIES} className="transition-opacity hover:opacity-60">
                Cookies
              </Link>
            </div>
            <div>
              © 2026 Camille Vega de Hoyos
            </div>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}